import React, { forwardRef, HTMLAttributes, useMemo } from "react";
import { useI18n } from "../../../../i18n/i18n";
import { CheckedState } from "../../../checkbox";
import { VisuallyHidden } from "../../../visuallyhidden";
import { MultiSelectOption } from "./MultiSelectOption";

export interface SelectAllOptionProps extends HTMLAttributes<HTMLDivElement> {
    checkedState: CheckedState;
    totalOptions: number;
    totalSelectedItems: number;
}

export const SelectAllOption = forwardRef<HTMLDivElement, SelectAllOptionProps>(
    ({ checkedState, totalSelectedItems, totalOptions, ...rest }, ref) => {
        const { i18n } = useI18n();
        const allOrSomeSelected = totalSelectedItems === totalOptions || totalSelectedItems > 0;
        const ariaLabel = useMemo(() => {
            switch (allOrSomeSelected) {
                case true:
                    return i18n._(
                        /*i18n*/ {
                            comment:
                                "This aria label is read to the user conveying the number of items selected. Then full stop. Then it conveys that clicking on the option will deselect all the items",
                            id: `{totalSelectedItems} selected. Deselect all`,
                            values: { totalSelectedItems },
                        }
                    );

                case false:
                    return i18n._(
                        /*i18n*/ {
                            comment: "This aria label conveys that clicking on the option will select all the items",
                            id: `{totalSelectedItems} selected. Select all {totalOptions} items`,
                            values: { totalSelectedItems, totalOptions },
                        }
                    );
            }
        }, [allOrSomeSelected, totalOptions, totalSelectedItems, i18n]);

        return (
            <MultiSelectOption
                checkedState={checkedState}
                ref={ref}
                onClick={(e) => {
                    rest.onClick?.(e);
                }}
                aria-label={ariaLabel}
                {...rest}
            >
                {totalSelectedItems === 0
                    ? i18n._(/*i18n*/ "Select All")
                    : i18n._(
                          /*i18n*/ {
                              comment: "This is to show the number of selected items",
                              id: `{totalSelectedItems} selected`,
                              values: { totalSelectedItems },
                          }
                      )}
                <VisuallyHidden>{i18n._(/*i18n*/ "Press Enter to select or deselect an option.")}</VisuallyHidden>
            </MultiSelectOption>
        );
    }
);

SelectAllOption.displayName = "SelectAllOption";
